import * as styles from "./DatePickerTrigger.css";

import { clsx } from "clsx";
import type { KeyboardEvent, PointerEvent } from "react";

import { Popover as BasePopover } from "@base-ui/react/popover";
import type { BaseUIEvent } from "@base-ui/react/types";
import { Button } from "@frontend/components/atoms/Button";
import { Icon } from "@frontend/components/atoms/Icon";
import { pointerOffsetIn } from "@frontend/utils/pointerAlign";

import { useDatePickerContext } from "./DatePicker.context";
import type { DatePickerTriggerProps } from "./DatePicker.types";

export function DatePickerTrigger({
  className,
  children,
  onPointerDown,
  onKeyDown,
  ...props
}: DatePickerTriggerProps) {
  const { size, layout, disabled, pointerOffsetRef } = useDatePickerContext();

  const isText = layout === "text";

  function handlePointerDown(
    event: BaseUIEvent<PointerEvent<HTMLButtonElement>>,
  ) {
    pointerOffsetRef.current = pointerOffsetIn(
      event.currentTarget,
      event.clientX,
    );

    onPointerDown?.(event);
  }

  function handleKeyDown(event: BaseUIEvent<KeyboardEvent<HTMLButtonElement>>) {
    if (event.key === "Enter" || event.key === " ") {
      // Opened via keyboard, no click position to open under: center it.
      pointerOffsetRef.current = 0;
    }

    onKeyDown?.(event);
  }

  if (layout === "icon") {
    return (
      <BasePopover.Trigger
        data-slot="date-picker-trigger"
        disabled={disabled}
        className={className}
        render={
          <Button
            variant="neutral"
            size={size}
            leftSection={<Icon name="CalendarSearch" size={size ?? "sm"} />}
          />
        }
        onPointerDown={handlePointerDown}
        onKeyDown={handleKeyDown}
        {...props}
      />
    );
  }

  return (
    <BasePopover.Trigger
      data-slot="date-picker-trigger"
      disabled={disabled}
      className={clsx(
        isText ? styles.textTrigger : styles.trigger({ size }),
        className,
      )}
      onPointerDown={handlePointerDown}
      onKeyDown={handleKeyDown}
      {...props}
    >
      {children}
      <span className={isText ? styles.textIcon : styles.icon}>
        <Icon name="Calendar" />
      </span>
    </BasePopover.Trigger>
  );
}
